import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProjectWizardLayout } from '../components/Wizard/ProjectWizardLayout';
import { WizardStepSelect } from '../components/Wizard/WizardStepSelect';
import { useWizardStore } from '../store/wizardStore';
import wizardSchema from '../data/wizardSchema.json';

export const ProjectWizardStepPage: React.FC = () => {
  const { stepIndex } = useParams<{ stepIndex: string }>();
  const navigate = useNavigate();
  const {
    currentDraft,
    isLoading,
    error,
    createDraft,
    loadDraft,
    saveAnswer,
    autoSave
  } = useWizardStore();

  const [currentValue, setCurrentValue] = useState('');
  const [validationError, setValidationError] = useState('');

  const currentStepIndex = parseInt(stepIndex || '1', 10);
  const currentStep = wizardSchema.steps[currentStepIndex - 1];
  const totalSteps = wizardSchema.steps.length;

  // Initialize or load draft
  useEffect(() => {
    const initializeDraft = async () => {
      // Check if we have a draft ID in localStorage
      const savedDraftId = localStorage.getItem('currentDraftId');
      
      if (savedDraftId) {
        try {
          await loadDraft(savedDraftId);
        } catch (error) {
          console.error('Failed to load draft:', error);
          // Create new draft if loading fails
          const newDraftId = await createDraft();
          localStorage.setItem('currentDraftId', newDraftId);
        }
      } else {
        // Create new draft
        const newDraftId = await createDraft();
        localStorage.setItem('currentDraftId', newDraftId);
      }
    };

    if (!currentDraft) {
      initializeDraft();
    }
  }, [currentDraft, loadDraft, createDraft]);

  // Load current answer when draft or step changes
  useEffect(() => {
    if (currentDraft && currentStep) {
      const savedAnswer = currentDraft.answers[currentStep.key] || '';
      setCurrentValue(savedAnswer);
    }
  }, [currentDraft, currentStep]);

  // Validate step index
  if (!currentStep || currentStepIndex < 1 || currentStepIndex > totalSteps) {
    navigate('/projects/new');
    return null;
  }

  const handleValueChange = (value: string) => {
    setCurrentValue(value);
    setValidationError('');
  };

  const handleNext = async () => {
    if (!currentValue.trim()) {
      setValidationError('Veuillez sélectionner une option.');
      return;
    }

    if (!currentDraft) return;

    try {
      await saveAnswer(currentStepIndex, currentStep.key, currentValue);
      
      if (currentStepIndex === totalSteps) {
        // Go to documents page
        navigate('/projects/new/documents');
      } else {
        // Go to next step
        navigate(`/projects/new/step/${currentStepIndex + 1}`);
      }
    } catch (error) {
      console.error('Failed to save answer:', error);
      setValidationError('Erreur lors de la sauvegarde. Veuillez réessayer.');
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 1) {
      navigate(`/projects/new/step/${currentStepIndex - 1}`);
    } else {
      navigate('/projects/new');
    }
  };

  const handleSave = async () => {
    if (!currentDraft || !currentValue.trim()) return;

    try {
      await autoSave(currentDraft.id, { ...currentDraft.answers, [currentStep.key]: currentValue }, currentStepIndex);
    } catch (error) {
      console.error('Manual save failed:', error);
    }
  };

  if (!currentStep) {
    return null;
  }

  return (
    <ProjectWizardLayout
      currentStep={currentStepIndex}
      totalSteps={totalSteps}
      onPrevious={handlePrevious}
      onNext={handleNext}
      onSave={handleSave}
      canGoNext={!!currentValue.trim()}
      canGoPrevious={currentStepIndex > 1}
      isLoading={isLoading}
      title="Création de projet ESS"
    >
      <WizardStepSelect
        label={currentStep.label}
        value={currentValue}
        options={currentStep.options}
        type={currentStep.type}
        placeholder={currentStep.placeholder}
        onChange={handleValueChange}
        error={validationError}
        required={currentStep.required}
        help={currentStep.help}
        example={currentStep.example}
      />
      
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mx-8 mb-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}
    </ProjectWizardLayout>
  );
};